import React from 'react';
import momentPropTypes from 'react-moment-proptypes';
import PropTypes from 'prop-types';
import cx from 'classnames';

import moment from 'moment';
import Button from '../Button';
import {
  CLOSED,
  SOLD_OUT,
} from '../../utils/calendarStateDeterminer';
import { getTranslation } from '../../providers/TranslationProvider';

const getStatusText = (status, date) => {
  const dateFormatted = date.format('dddd, LL');

  switch (status) {
    case CLOSED: {
      const closedCopy = getTranslation({
        translationKey: 'calendar_day_button_closed',
        translationValues: { date: dateFormatted },
        fallback: `${dateFormatted}, closed.`,
      });
      return closedCopy;
    }
    case SOLD_OUT: {
      const soldOutCopy = getTranslation({
        translationKey: 'calendar_day_button_sold_out',
        translationValues: { date: dateFormatted },
        fallback: `${dateFormatted} is sold out.`,
      });
      return soldOutCopy;
    }
    default:
      return getTranslation({
        translationKey: 'calendar_day_button_available',
        translationValues: { date: dateFormatted },
        fallback: `${dateFormatted}.`,
      });
  }
};

// Tuesday, February 23rd, closed. [Selected date.]
// Tuesday, February 23rd is sold out. [Selected date.]
// Show booking times for Tuesday, February 23rd. [Selected date.]
const getAriaLabel = (date, status, selected, hasEvent) => {
  let extraLabels = '';
  const a11yStatusStr = getStatusText(status, date);
  const a11ySelectedDateStr = getTranslation({
    translationKey: 'calendar_day_button_selected',
    fallback: 'Selected date.',
  });
  const a11yHasEvent = getTranslation({
    translationKey: 'calendar_day_button_has_event',
    fallback: 'Has event.',
  });

  if (selected) {
    extraLabels += ` ${a11ySelectedDateStr}`;
  }

  if (hasEvent) {
    extraLabels += ` ${a11yHasEvent}`;
  }

  return `${a11yStatusStr}${extraLabels}`;
};

const renderEvent = () => <div className="ResyCalendar-day--event" />;

const DayButton = ({
  className,
  date,
  id,
  onClick,
  selected,
  status,
}) => {
  const hasEvent = status?.event === 'available' || status?.event === 'sold-out';
  const isAvailable = status?.reservation === 'available';
  const allClosed = status?.event === 'closed' && status?.reservation === 'closed' && status['walk-in'] === 'closed';
  const disabled = {
    disabled: allClosed || status === 'outside-window',
  };
  const noData = status === 'no-data';
  const notAvailable = status?.reservation === 'sold-out' || status?.reservation === 'not available';
  const limitedAvailability = notAvailable && status?.event === 'available';

  const statusStyles = cx({
    'ResyCalendar-day--selected__available': selected && (isAvailable || limitedAvailability || noData),
    'ResyCalendar-day--selected__unavailable': selected && notAvailable,
    'ResyCalendar-day--available--has-event': !selected && isAvailable && hasEvent,
    'ResyCalendar-day--outside-window': allClosed || (!selected && status === 'outside-window'),
    'ResyCalendar-day--partially-available': !selected && limitedAvailability,
    'ResyCalendar-day--available': !selected && isAvailable,
    'ResyCalendar-day--sold-out': !selected && !limitedAvailability && notAvailable,
    'ResyCalendar-day--no-data': !selected && noData,
  });

  const clickHandler = () => onClick(date.format('YYYY-MM-DD'));

  return date && (
    <Button
      aria-label={getAriaLabel(date, status, selected, hasEvent)}
      {...disabled}
      circle
      className={`ResyCalendar-day ${statusStyles && statusStyles} ${className}`}
      id={id}
      onClick={clickHandler}
      data-test-id="ResyCalendar-day-data"
    >
      &nbsp;
      {date.format('D')}
&nbsp;
      {hasEvent && renderEvent()}
    </Button>
  );
};

const STATUSES = ['available', 'outside-window', 'not available', 'closed'];

DayButton.propTypes = {
  className: PropTypes.string,
  date: momentPropTypes.momentObj,
  id: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  status: PropTypes.oneOfType([
    PropTypes.shape({
      event: PropTypes.oneOf(STATUSES),
      reservation: PropTypes.oneOf(STATUSES),
      'walk-in': PropTypes.oneOf(STATUSES),
    }).isRequired,
    PropTypes.oneOf(STATUSES),
  ]),
  selected: PropTypes.bool.isRequired,
};

DayButton.defaultProps = {
  className: undefined,
  date: moment(),
  id: undefined,
  status: 'outside-window',
};
DayButton.displayName = 'DayButton';

export default DayButton;
